import { getBackendUrl } from "./api";

/**
 * Resolves an image URL to a full URL.
 * - Relative URLs (/api/uploads/...) are prefixed with backend URL
 * - Absolute URLs (https://...) are returned as-is
 * - Empty/null values return a placeholder
 */
export const resolveImageUrl = (url) => {
  if (!url) {
    return "https://via.placeholder.com/400x300?text=No+Image";
  }
  
  // If it's already an absolute URL (or a base64 data URI), return as-is
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
    return url;
  }
  
  // Relative path — always resolve against the backend root, regardless of
  // whether it already has a leading slash (a path missing one, e.g. a
  // hand-typed `api/uploads/x.jpg`, would otherwise resolve relative to the
  // current page path in a browser instead of the site root).
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${getBackendUrl()}${path}`;
};

/**
 * Gets optimized image URL for responsive loading.
 * Uses server-side image optimization endpoint.
 * 
 * @param {string} url - Original image URL
 * @param {number} width - Desired width (640 for mobile, 1024 for tablet, 1920 for desktop)
 * @param {number} quality - Quality 10-100 (default 75)
 * @returns {string} - Optimized image URL
 */
export const getOptimizedImageUrl = (url, width = 800, quality = 75) => {
  if (!url) {
    return "https://via.placeholder.com/400x300?text=No+Image";
  }
  
  // For external URLs (Unsplash, etc.), use their native optimization
  if (url.includes('unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&q=${quality}`;
  }
  
  // For local uploads, use our optimization endpoint
  if (url.startsWith("/api/uploads/")) {
    const filename = url.replace("/api/uploads/", "");
    return `${getBackendUrl()}/api/img/${filename}?w=${width}&q=${quality}`;
  }
  
  // For other relative paths
  if (url.startsWith("/uploads/")) {
    const filename = url.replace("/uploads/", "");
    return `${getBackendUrl()}/api/img/${filename}?w=${width}&q=${quality}`;
  }
  
  // Return resolved URL for external images
  return resolveImageUrl(url);
};

/**
 * Generates srcset for responsive images
 * 
 * @param {string} url - Original image URL  
 * @returns {string|undefined} - srcset string or undefined if not applicable
 */
export const getResponsiveSrcSet = (url) => {
  if (!url) return undefined;
  
  // For Unsplash images
  if (url.includes('unsplash.com')) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}w=640&q=75 640w, ${url}${sep}w=1024&q=75 1024w, ${url}${sep}w=1920&q=75 1920w`;
  }
  
  // For local uploads
  if (url.startsWith("/api/uploads/") || url.startsWith("/uploads/")) {
    const filename = url.replace("/api/uploads/", "").replace("/uploads/", "");
    const base = `${getBackendUrl()}/api/img/${filename}`;
    return `${base}?w=640&q=75 640w, ${base}?w=1024&q=75 1024w, ${base}?w=1920&q=75 1920w`;
  }
  
  return undefined;
};

export default resolveImageUrl;
